import { CheckRounded, CloseRounded, DeleteOutlineRounded } from "@mui/icons-material";
import { Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useUser from "../../../../providers/user/user_provider";
import { ConfirmationDialog } from "../../../../components/common/confirmation-dialog";
import { JobListingModel } from "../../model/job_listing_model";
import {
  TableCell,
  TableEmptyState,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import { black, green, greenLight, greyEnabled, purple, red, redLight } from "../../../../theme/colors";

interface DataGridInterface {
  data: JobListingModel[];
  page: number;
  pageSize: number;
  setSelectedJob: (val: JobListingModel) => void;
  updateServiceApiCall: (params: { statusId: number; jobId: number; comments: string }) => void;
  setRejectDialogOpen: (val: boolean) => void;
  setDeleteDialogOpen: (val: boolean) => void;
  actionLoading: { jobId: number; action: "approve" | "reject" | "delete" } | null;
}

const HEADER_LABELS = [
  { label: "Sl. No.", flex: 0.35 },
  { label: "Job ID", flex: 0.5 },
  { label: "Position / Company", flex: 1.4 },
  { label: "Location", flex: 1 },
  { label: "Experience", flex: 0.7 },
  { label: "Contact", flex: 0.9 },
  { label: "Status", flex: 0.8 },
  { label: "Created From", flex: 0.7 },
  { label: "Expiry", flex: 0.7 },
  { label: "Listed On", flex: 0.7 },
  { label: "Actions" },
];

function statusColor(status: string) {
  if (status === "Modified" || status === "Pending" || status === "In Progress") return purple;
  if (status === "Approved") return green;
  return red;
}

export default function DataGridJobListing({
  data,
  page,
  pageSize,
  setSelectedJob,
  updateServiceApiCall,
  setRejectDialogOpen,
  setDeleteDialogOpen,
  actionLoading,
}: DataGridInterface) {
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<JobListingModel>();

  const handleApproveConfirm = () => {
    if (selectedItem) {
      updateServiceApiCall({ statusId: 3, jobId: +selectedItem.id, comments: "" });
    }
    setConfirmOpen(false);
  };

  function actionButtons(item: JobListingModel) {
    const status = item.jobStatus;
    const canUpdate = hasPermission("jobs", "update");
    const canDelete = hasPermission("jobs", "delete");
    const isRowLoading = actionLoading?.jobId === +item.id;
    const isApproving = isRowLoading && actionLoading?.action === "approve";
    const isRejecting = isRowLoading && actionLoading?.action === "reject";
    const isDeleting = isRowLoading && actionLoading?.action === "delete";

    if (["Pending", "Modified", "Submitted", "In Progress"].includes(status)) {
      return (
        <Box display="flex" gap={0.5}>
          {canUpdate && (
            <Tooltip title="Approve">
              <span>
                <IconButton
                  disabled={isRowLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedItem(item);
                    setConfirmOpen(true);
                  }}
                  sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                >
                  {isApproving ? (
                    <CircularProgress size={18} sx={{ color: green }} />
                  ) : (
                    <CheckRounded sx={{ color: green, fontSize: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title="Reject">
              <span>
                <IconButton
                  disabled={isRowLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedJob(item);
                    setRejectDialogOpen(true);
                  }}
                  sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
                >
                  {isRejecting ? (
                    <CircularProgress size={18} sx={{ color: red }} />
                  ) : (
                    <CloseRounded sx={{ color: red, fontSize: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    }

    if (["Rejected", "Approved", "Closed"].includes(status)) {
      return (
        <Box display="flex" gap={0.5}>
          {canUpdate && (
            <Tooltip title="Approved">
              <span>
                <IconButton disabled sx={{ bgcolor: greenLight, opacity: 0.5 }}>
                  <CheckRounded sx={{ color: green, fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canDelete && status === "Approved" && (
            <Tooltip title="Delete">
              <span>
                <IconButton
                  disabled={isRowLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedJob(item);
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
                >
                  {isDeleting ? (
                    <CircularProgress size={18} sx={{ color: red }} />
                  ) : (
                    <DeleteOutlineRounded sx={{ color: red, fontSize: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    }

    return null;
  }

  if (data.length === 0) {
    return <TableEmptyState message="No jobs found" hint="Try adjusting your search or filters." />;
  }

  return (
    <Box mt={1}>
      <TableHeaderRow labels={HEADER_LABELS} lastColumnWidth={110} bordered />
      {data.map((item, index) => (
        <TableRow key={item.id} bordered onClick={() => navigate(`/jobs/jobsDetails/${item.id}`)}>
          <TableCell label="Sl. No." flex={0.35} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {(page - 1) * pageSize + index + 1}
            </Typography>
          </TableCell>
          <TableCell label="Job ID" flex={0.5} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.id ?? "-"}
            </Typography>
          </TableCell>
          <TableCell label="Position / Company" flex={1.4} bordered>
            <Typography fontWeight={600} color={black} noWrap>
              {item.jobPosition || "-"}
            </Typography>
            <Typography variant="body2" color={greyEnabled} noWrap>
              {[item.company, item.jobType].filter(Boolean).join(" · ") || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Location" flex={1} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {[item.city, item.district, item.state].filter(Boolean).join(", ") || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Experience" flex={0.7} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.workExperience || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Contact" flex={0.9} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.jobContactNo ? `+91 ${item.jobContactNo}` : "-"}
            </Typography>
          </TableCell>
          <TableCell label="Status" flex={0.8} bordered>
            <Typography fontSize={14} fontWeight={600} color={statusColor(item.jobStatus)} noWrap>
              {item.jobStatus}
            </Typography>
          </TableCell>
          <TableCell label="Created From" flex={0.7} bordered>
            <Chip
              size="small"
              label={item.source || "Admin"}
              sx={{
                bgcolor: item.source === "App" ? "#E9F0FB" : "#F5E6F5",
                color: item.source === "App" ? "#2F5FA8" : purple,
                fontWeight: 600,
              }}
            />
          </TableCell>
          <TableCell label="Expiry" flex={0.7} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.expiryDate || "-"}
            </Typography>
          </TableCell>
          <TableCell label="Listed On" flex={0.7} bordered>
            <Typography fontSize={14} color={black} noWrap>
              {item.createdAt ? item.createdAt.split("T")[0] : "-"}
            </Typography>
          </TableCell>
          <TableCell isLast lastColumnWidth={110} bordered>
            <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
              {actionButtons(item)}
            </Box>
          </TableCell>
        </TableRow>
      ))}
      <ConfirmationDialog
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleApproveConfirm}
        open={confirmOpen}
        selectedItem={selectedItem}
        bodyContent="Are you sure want to approve this job?"
      />
    </Box>
  );
}
